import { useEffect, useMemo, useState } from 'react';
import { composerMailExpeditionTexte, LIBELLE_LANGUE } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useDeposerBrouillon, useReglages } from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { IgnorerMailClient } from './IgnorerMailClient';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/**
 * Relecture de l'annonce d'expédition avant envoi.
 *
 * Ce mail était le dernier que n8n envoyait — et il l'ENVOYAIT, sans relecture.
 * Il est désormais composé ici, en cinq langues au lieu de deux, avec une date
 * écrite dans la langue du client, relu ici puis envoyé depuis contact@ comme
 * les autres. Cette fenêtre est la seule relecture.
 */
export function MailExpedition({
  commande, onFermer,
}: {
  commande: CommandeListee;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  // Les réglages portent les modèles : sans eux, l'écran montrerait le texte
  // du code pendant que le serveur enverrait celui de Réglages.
  const { data: reglages } = useReglages();
  const deposer = useDeposerBrouillon();

  const modele = useMemo(() => composerMailExpeditionTexte({
    ref: commande.ref,
    dateExpedition: commande.dateExpedition,
    langue: commande.langue,
  }, reglages), [commande, reglages]);

  const [to, setTo] = useState(commande.email ?? '');
  const [objet, setObjet] = useState(modele.objet);
  const [corps, setCorps] = useState(modele.corps);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setTo(commande.email ?? ''); setObjet(modele.objet); setCorps(modele.corps);
  }, [modele, commande.email]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  async function envoyer() {
    setErreur(null);
    try {
      const r = await deposer.mutateAsync({
        id: commande.id,
        canal: 'expedition',
        corps: { destinataire: to, objet, message: corps },
      });

      if (!r.brouillon.depose) {
        setErreur(r.brouillon.erreur ?? "Le mail n'a pas pu être envoyé.");
        return;
      }
      ajouter(
        r.brouillon.simule
          ? 'Mail composé, mais Outlook est en mode local : aucun mail envoyé.'
          : `Mail d'expédition envoyé à ${commande.client}.`,
        r.brouillon.simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'envoi a échoué.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Mail d'expédition ${commande.ref}`}>
        <div className="mhead">
          <h2>Mail d&apos;expédition — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <div className="field">
            <label htmlFor="me-to">
              Destinataire
              <span className="tag" style={{ marginLeft: 8 }}>{LIBELLE_LANGUE[modele.langue]}</span>
            </label>
            <input
              id="me-to" type="text" value={to} placeholder="adresse du client"
              onChange={(e) => setTo(e.target.value)}
            />
            {!commande.email && (
              <p className="err">
                Cette commande n&apos;a pas d&apos;adresse client : saisissez-la, sans quoi le
                mail ne peut pas partir.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="me-obj">Objet</label>
            <input id="me-obj" value={objet} onChange={(e) => setObjet(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="me-corps">Message</label>
            <textarea id="me-corps" rows={14} value={corps} onChange={(e) => setCorps(e.target.value)} />
            <p className="indice">
              La date est écrite dans la langue du client : « 29 juin », « 29 June », « 29 giugno ».
            </p>
          </div>

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Le mail <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ :
            relisez-le ici, il n&apos;y a pas d&apos;autre relecture.
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          {/* Prévenir n'est pas obligatoire : l'étape peut être tenue pour
              faite sans mail, depuis la fenêtre même où l'on en décide. */}
          <IgnorerMailClient commande={commande} canal="expedition" onFait={onFermer} />
          <button
            type="button" className="btn pri" disabled={deposer.isPending}
            onClick={() => void envoyer()}
          >
            {deposer.isPending ? <><span className="spin" /> Envoi…</> : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
